<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useRoute } from 'vue-router'

import type { ProductDto } from '@/stores/buildingBlock'
import { useProductStore } from '@/stores/product'
import { useCalculationEnvironmentDatabaseSelectionStore } from '@/stores/calculationEnvironmentDatabaseSelection'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'

const props = defineProps<{
  title: string
  subtitle: string
  searchLabel: string
  noMatchLabel: string
  emptySearchLabel: string
  confirmText: string
  cancelText?: string
  readonly?: boolean
  addLoading?: boolean
}>()

const dialog = defineModel<boolean>({ required: true })

const route = useRoute()
const environmentDatabaseSelectionStore = useCalculationEnvironmentDatabaseSelectionStore()
const calculationId = computed(() => Number(route.params.calculationId))
const selectedEnvironmentDatabase = computed(() =>
  environmentDatabaseSelectionStore.getSelectedDatabase(calculationId.value)
)

const productStore = useProductStore()
const { searchProductBaseStore } = productStore

const selectedProduct = ref<ProductDto | null>(null)
const searchText = ref('')

const emit = defineEmits<{
  select: [product: ProductDto]
}>()

const clearSearch = () => {
  searchText.value = ''
  selectedProduct.value = null
  searchProductBaseStore.resetSearchState()
}

const closeDialog = () => {
  clearSearch()
  dialog.value = false
}

const confirmSelection = () => {
  if (props.readonly) return
  if (selectedProduct.value) {
    emit('select', selectedProduct.value)
  }
  closeDialog()
}

const search = useDebounceFn(async (text: string) => {
  searchText.value = text
  if (text && selectedEnvironmentDatabase.value) {
    await searchProductBaseStore.searchProducts(selectedEnvironmentDatabase.value, text)
    return
  }

  searchProductBaseStore.resetSearchState()
}, DEFAULT_DEBOUNCE_DELAY)

watch(dialog, (isVisible) => {
  if (!isVisible) {
    clearSearch()
  }
})
</script>

<template>
  <v-dialog v-model="dialog" max-width="600">
    <v-card class="pa-3 pt-6">
      <v-card-title>{{ props.title }}</v-card-title>
      <v-card-subtitle>{{ props.subtitle }}</v-card-subtitle>
      <v-card-item class="pa-4">
        <v-combobox
          v-model="selectedProduct"
          color="primary"
          :hide-no-data="false"
          :items="searchProductBaseStore.searchResults"
          item-value="id"
          item-title="name"
          :loading="searchProductBaseStore.searchLoading"
          variant="underlined"
          hide-selected
          persistent-hint
          :clearable="true"
          :label="props.searchLabel"
          lines="three"
          :readonly="props.readonly"
          :disabled="props.readonly || !selectedEnvironmentDatabase"
          @update:search="search"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item style="max-width: 500px" v-bind="itemProps">
              <v-list-item-subtitle class="text-wrap">
                {{ item.raw.description }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
          <template #no-data>
            <v-list-item>
              <v-list-item-title v-if="searchText">
                {{ props.noMatchLabel }} "<strong>{{ searchText }}</strong
                >"
              </v-list-item-title>
              <v-list-item-title v-else-if="!selectedEnvironmentDatabase">
                Select environmental database first in Environmental Database tab
              </v-list-item-title>
              <v-list-item-title v-else>
                {{ props.emptySearchLabel }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-combobox>
      </v-card-item>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="text-none"
          :disabled="props.addLoading || props.readonly"
          @click="closeDialog"
        >
          {{ props.cancelText ?? 'Cancel' }}
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          :loading="props.addLoading"
          :disabled="!selectedProduct?.id || props.readonly"
          @click="confirmSelection"
        >
          {{ props.confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
